import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Close";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import UserAvatar from "./components/UserAvatar";
import apiClient from "./utils/apiClient";
import { formatDate } from "./DateUtils";
import { buildCedexResultQuery, normalizeRequestRecords } from "./utils/cedex";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Unable to read file"));
        return;
      }
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = () => reject(new Error("Failed reading file"));
    reader.readAsDataURL(file);
  });
}

function base64ToBlob(base64) {
  const binary = atob(base64.replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes]);
}

export default function CedexImport() {
  const { importId: routeImportId } = useParams();
  const [imports, setImports] = useState([]);
  const [selectedImportId, setSelectedImportId] = useState(routeImportId || null);
  const [selectedImport, setSelectedImport] = useState(null);
  const [results, setResults] = useState([]);
  const [analytes, setAnalytes] = useState([]);
  const [showAnalytes, setShowAnalytes] = useState(false);
  const [sampleFilter, setSampleFilter] = useState("");
  const [analyteFilter, setAnalyteFilter] = useState("");
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [isImporting, setIsImporting] = useState(false);
  // Vi-CELL (Beckman cell-growth Summary CSV) merger state — files are
  // per-day, one file per timepoint; typical STRATYX-style batch is 12
  // files (D0..D11).
  const [vicellFiles, setVicellFiles] = useState([]);
  const vicellInputRef = useRef(null);
  const [isMergingVicell, setIsMergingVicell] = useState(false);
  const [vicellSummary, setVicellSummary] = useState(null);
  const [isUpdatingVicell, setIsUpdatingVicell] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastInputDefinitionId, setLastInputDefinitionId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  // Selecting an import pushes its id into the URL so the row/detail view
  // is a shareable link (e.g. /cedex-import/<id>) — copy/paste reopens the
  // same parsed-results view for another user.
  const selectImport = (id, { replace = false } = {}) => {
    setSelectedImportId(id || null);
    navigate(id ? `/cedex-import/${id}` : "/cedex-import", { replace });
  };

  // Browser back/forward (or a direct link with a different id) changes
  // routeImportId without re-mounting the component — mirror it into
  // selection state so the detail panel follows the URL.
  useEffect(() => {
    if ((routeImportId || null) !== selectedImportId) {
      setSelectedImportId(routeImportId || null);
    }
  }, [routeImportId]);

  const selectedImportSummary = useMemo(() => {
    if (!selectedImport) {
      return null;
    }
    return `status=${selectedImport.status || "unknown"} results=${selectedImport.result_count || 0} warnings=${selectedImport.warning_count || 0} errors=${selectedImport.error_count || 0}`;
  }, [selectedImport]);

  const multiReadingDays = selectedImport?.validation_summary?.multi_reading_days || [];
  const warningGroups = selectedImport?.validation_summary?.warning_groups || [];
  const vicellStoredFiles = selectedImport?.vicell_files || [];

  const loadImports = async () => {
    const response = await apiClient.post("/cedex-import-request", { action: "list", limit: 50 });
    setImports(normalizeRequestRecords(response));
  };

  const loadImportDetail = async (importId) => {
    const response = await apiClient.post("/cedex-import-request", { action: "detail", id: importId });
    const records = normalizeRequestRecords(response);
    const record = records.length ? records[0] : null;
    setSelectedImport(record);
  };

  const loadResults = async (importId, sampleLike = "", analyteLike = "") => {
    const response = await apiClient.post("/cedex-result-request", buildCedexResultQuery(importId, sampleLike, analyteLike));
    setResults(normalizeRequestRecords(response));
  };

  const loadAnalytes = async () => {
    const response = await apiClient.post("/cedex-analyte-request", { action: "list", limit: 500 });
    setAnalytes(normalizeRequestRecords(response));
  };

  useEffect(() => {
    loadImports();
  }, []);

  useEffect(() => {
    if (!selectedImportId) {
      setSelectedImport(null);
      setResults([]);
      return;
    }
    loadImportDetail(selectedImportId);
    loadResults(selectedImportId);
  }, [selectedImportId]);

  useEffect(() => {
    if (showAnalytes && analytes.length === 0) {
      loadAnalytes();
    }
  }, [showAnalytes]);

  const handleImport = async () => {
    if (!files.length) {
      return;
    }
    try {
      setIsImporting(true);
      setMessage(null);
      // Several files are one run split across archives (e.g. D0 in its
      // own file): the backend joins them into one import.
      const encoded = await Promise.all(files.map(async (f) => ({ filename: f.name, data: await fileToBase64(f) })));
      const response = await apiClient.post("/cedex-import-request", encoded.length === 1
        ? { action: "import", filename: files[0].name, file_type: files[0].type || "text/plain", data: encoded[0].data }
        : { action: "import", files: encoded });
      const importId = response?.metadata?.import_id || response?.records?.[0]?.id;
      const inputDefinitionId = response?.metadata?.input_definition_id;
      const coverage = response?.metadata?.mapping_coverage;
      setLastInputDefinitionId(inputDefinitionId || null);
      const mappedN = coverage?.mapped_codes?.length || 0;
      const unmappedN = coverage?.unmapped_codes?.length || 0;
      const totalN = mappedN + unmappedN;
      const coverageSuffix = totalN
        ? ` Analyte coverage: ${mappedN}/${totalN} codes mapped to MFALite_data` +
          (unmappedN ? ` (unmapped: ${coverage.unmapped_codes.slice(0, 5).join(", ")}${unmappedN > 5 ? "…" : ""})` : "")
        : "";
      // Scoped per sample_id on the backend — each entry is one sample
      // whose OWN measurements span more than one reading session, never
      // two different samples that happen to fall on the same day.
      const multiReadingDays = response?.metadata?.multi_reading_days || [];
      const multiReadingSuffix = multiReadingDays.length
        ? " " + multiReadingDays.map((d) => {
            const times = (d.cluster_start_times_ms || []).map((ms) => formatDate(ms)).join(", ");
            return `⚠ Sample ${d.sample_id}, Day ${d.day} has ${d.cluster_count} separate reading sessions (${times}) — confirm with the operator which is authoritative.`;
          }).join(" ")
        : "";
      // Checksum match against a prior import is informational only — the
      // backend always re-parses and creates a fresh history record (there
      // are real reasons to re-import identical bytes, e.g. a parser fix),
      // so this is just a heads-up, never a block.
      const duplicateOfId = response?.metadata?.duplicate_of;
      const duplicateSuffix = duplicateOfId
        ? ` Note: identical content to a prior import ("${response?.metadata?.duplicate_of_filename || duplicateOfId}"` +
          (response?.metadata?.duplicate_of_imported_at
            ? `, uploaded ${formatDate(response.metadata.duplicate_of_imported_at)}`
            : "") +
          `) — re-parsed anyway as a new history record.`
        : "";
      setMessage({
        type: multiReadingDays.length ? "warning" : "success",
        text: `Imported ${files.map((f) => f.name).join(" + ")}. ${response?.metadata?.result_count || 0} rows parsed.${inputDefinitionId ? " Added to Input Library." : ""}${coverageSuffix}${multiReadingSuffix}${duplicateSuffix}`,
      });
      await loadImports();
      if (importId) {
        selectImport(importId);
      }
    } catch (error) {
      setMessage({ type: "error", text: error?.message || "Import failed." });
    } finally {
      setIsImporting(false);
    }
  };

  // Changes the server rebuilds the import's rows from — the first
  // measurement day, a stored Vi-CELL file's day, removing a file. The
  // stored Vi-CELL files merge again as part of the rebuild.
  const runVicellRebuild = async (body, doneText) => {
    if (!selectedImport?.id) return;
    try {
      setIsUpdatingVicell(true);
      setMessage(null);
      const response = await apiClient.post("/cedex-import-request", {
        cedex_import_id: selectedImport.id,
        ...body,
      });
      const meta = response?.metadata || {};
      if (meta.status !== "success") {
        throw new Error(meta.message || "Update failed.");
      }
      setVicellSummary({
        rows_updated: meta.vicell_rows_updated ?? 0,
        files_processed: (meta.vicell_files || vicellStoredFiles).length,
        warnings: meta.warnings || [],
        suggested_first_measurement_day: meta.suggested_first_measurement_day,
      });
      setMessage({ type: "success", text: doneText });
      await loadImportDetail(selectedImport.id);
    } catch (error) {
      setMessage({ type: "error", text: error?.message || "Update failed." });
    } finally {
      setIsUpdatingVicell(false);
    }
  };

  const handleSetFirstDay = (day) =>
    runVicellRebuild(
      { action: "first_measurement_day_save", first_measurement_day: day },
      `Days now numbered from day ${day}; Vi-CELL files merged again.`
    );

  const handleVicellDayChange = (filename, value) => {
    const day = Number(value);
    if (value === "" || !Number.isInteger(day) || day < 0) return;
    runVicellRebuild(
      { action: "vicell_file_update", filename, day },
      `${filename}: day set to ${day}.`
    );
  };

  const handleVicellRemove = (filename) =>
    runVicellRebuild(
      { action: "vicell_file_update", filename, remove: true },
      `Removed ${filename}.`
    );

  const applyFilters = async () => {
    if (!selectedImportId) {
      return;
    }
    await loadResults(selectedImportId, sampleFilter, analyteFilter);
  };

  // Vi-CELL merger: takes one or more Beckman Vi-CELL Summary CSVs
  // (per-day cell growth data) and merges VCD / Viability /
  // Cell_diameter into the raw_sheet_row rows for the currently-selected
  // CEDEX import. Filename encodes the process day (D<N>); Sample ID
  // (1..4) encodes the replicate. Backend action: import_vicell.
  const handleVicellFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setVicellFiles(files);
    setVicellSummary(null);
    setMessage(null);
    // reset the file input so re-selecting the same file works
    if (vicellInputRef.current) vicellInputRef.current.value = "";
  };

  const handleMergeVicell = async () => {
    if (!selectedImport?.id || !vicellFiles.length) return;
    try {
      setIsMergingVicell(true);
      setMessage(null);
      setVicellSummary(null);
      // Serialize each file to base64 in parallel — 12 small CSVs
      // typically finish in <100ms total.
      const encoded = await Promise.all(
        vicellFiles.map(async (f) => ({
          filename: f.name,
          data: await fileToBase64(f),
        }))
      );
      const response = await apiClient.post("/cedex-import-request", {
        action: "import_vicell",
        cedex_import_id: selectedImport.id,
        files: encoded,
      });
      const meta = response?.metadata || {};
      setVicellSummary(meta);
      const rows = meta.rows_updated ?? 0;
      const proc = meta.files_processed ?? 0;
      const warn = (meta.warnings || []).length;
      const rej = meta.files_rejected ?? 0;
      if (meta.suggested_first_measurement_day != null) {
        setMessage({
          type: "info",
          text: `Saved ${encoded.length} Vi-CELL file(s); not merged yet — see below.`,
        });
        setVicellFiles([]);
        await loadImportDetail(selectedImport.id);
        return;
      }
      setMessage({
        type: warn || rej ? "warning" : "success",
        text:
          `Merged Vi-CELL data — ${rows} row(s) updated across ${proc} file(s)` +
          (warn ? `, ${warn} warning(s)` : "") +
          (rej ? `, ${rej} file(s) rejected` : "") +
          ".",
      });
      // Clear the picker so a second merge doesn't accidentally re-post.
      setVicellFiles([]);
      // Refresh the current import's detail + result view so any status
      // update or new coverage numbers show through.
      await loadImportDetail(selectedImport.id);
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "Vi-CELL merge failed.",
      });
    } finally {
      setIsMergingVicell(false);
    }
  };

  // When either filter field is populated, "Generate Input File" builds a
  // fresh InputDefinition scoped to the filtered result set (server-side —
  // input-definition-request action="import_cedex" with sample_like /
  // analyte_like). Otherwise it navigates to the pre-created whole-archive
  // definition that CedexImport auto-created at upload time.
  const [isGenerating, setIsGenerating] = useState(false);
  const handleGenerateInputFile = async () => {
    if (!selectedImport) return;
    const sample = (sampleFilter || "").trim();
    const analyte = (analyteFilter || "").trim();
    const hasFilter = Boolean(sample || analyte);

    if (!hasFilter && selectedImport.input_definition_id) {
      navigate(`/input/${selectedImport.input_definition_id}`);
      return;
    }

    setIsGenerating(true);
    try {
      const res = await apiClient.post("/input-definition-request", {
        action: "import_cedex",
        cedex_import_id: selectedImport.id,
        sample_like: sample || undefined,
        analyte_like: analyte || undefined,
      });
      const meta = res?.metadata || {};
      const newId = meta.id;
      const status = meta.status;
      if (status === "error" || !newId) {
        setMessage({
          type: "error",
          text: meta.detail || "Failed to generate filtered input definition.",
        });
        return;
      }
      if (Array.isArray(meta.warnings) && meta.warnings.length) {
        setMessage({
          type: "info",
          text: `Generated with ${meta.warnings.length} warning(s). Open the wizard to review.`,
        });
      }
      navigate(`/input/${newId}`);
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "Failed to generate filtered input definition.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      const response = await apiClient.post("/cedex-import-request", {
        action: "download",
        id: item.id,
      });
      const meta = response?.metadata || {};
      if (meta.status === "error" || !meta.data) {
        setMessage({ type: "error", text: meta.message || "File is no longer available." });
        return;
      }
      const blob = base64ToBlob(meta.data);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = meta.filename || item.filename || "cedex-import";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ type: "error", text: error?.message || "Download failed." });
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget?.id) {
      return;
    }
    try {
      setIsDeleting(true);
      await apiClient.delete(`/cedex-import/${encodeURIComponent(deleteTarget.id)}`);
      setMessage({
        type: "success",
        text: `Deleted import ${deleteTarget.filename || deleteTarget.id} and its parsed results.`,
      });
      if (selectedImportId === deleteTarget.id) {
        selectImport(null, { replace: true });
      }
      setDeleteTarget(null);
      await loadImports();
    } catch (error) {
      setMessage({ type: "error", text: error?.message || "Delete failed." });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<ScienceIcon />}
          title="CEDEX Import"
          subtitle="Upload CEDEX export files and inspect parsed analyte results"
        />

        <DataCard title="Upload File">
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ backgroundColor: "background.paper" }}
            >
              Choose File(s)
            </Button>
            <Typography
              variant="body2"
              sx={{ color: files.length ? "text.primary" : "text.secondary" }}
            >
              {files.length ? files.map((f) => f.name).join(" + ") : "No file selected"}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!files.length || isImporting}
              startIcon={isImporting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isImporting ? "Importing..." : "Import File"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.csv"
              multiple
              style={{ display: "none" }}
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            One run split across several CEDEX files (for example D0 in its own file)? Choose them all:
            they become one import, with days counted from the earliest measurement.
          </Typography>
          {message && (
            <Alert
              sx={{ mt: 2 }}
              severity={["error", "warning"].includes(message.type) ? message.type : "success"}
              action={
                lastInputDefinitionId && message.type !== "error" ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => navigate(`/input/${lastInputDefinitionId}`)}
                  >
                    Generate Input File
                  </Button>
                ) : null
              }
            >
              {message.text}
            </Alert>
          )}
        </DataCard>

        <DataCard title="Import History" noPadding>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderCellSx} align="center" style={{ width: 56 }}>User</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Imported At</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Filename</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Status</TableCell>
                  <TableCell sx={tableHeaderCellSx} align="right">Rows</TableCell>
                  <TableCell sx={tableHeaderCellSx} align="right">Warnings</TableCell>
                  <TableCell sx={tableHeaderCellSx} align="right">Errors</TableCell>
                  <TableCell sx={tableHeaderCellSx} align="right" style={{ width: 96 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {imports.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    selected={selectedImportId === item.id}
                    onClick={() => selectImport(item.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell align="center" sx={{ width: 56 }}>
                      <UserAvatar email={item.user_id} />
                    </TableCell>
                    <TableCell>{formatDate(item.imported_at)}</TableCell>
                    <TableCell>{item.filename}</TableCell>
                    <TableCell>{item.status}</TableCell>
                    <TableCell align="right">{item.result_count || 0}</TableCell>
                    <TableCell align="right">{item.warning_count || 0}</TableCell>
                    <TableCell align="right">{item.error_count || 0}</TableCell>
                    <TableCell align="right" sx={{ width: 96 }}>
                      {item.has_file && (
                        <Tooltip title="Download file">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(item);
                            }}
                          >
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete import">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(item);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DataCard>

        <DataCard
          title="Normalized Analyte Reference"
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowAnalytes(!showAnalytes)}
            >
              {showAnalytes ? "Hide" : "Show"}
            </Button>
          }
          noPadding={showAnalytes}
        >
          {showAnalytes ? (
            <>
              <Box sx={{ px: 3, py: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  All normalized analyte codes used in the system. Total: {analytes.length}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeaderCellSx}>Code</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Name</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Category</TableCell>
                      <TableCell sx={tableHeaderCellSx}>CMFA ID</TableCell>
                      <TableCell sx={tableHeaderCellSx}>MFALite Column</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Unit (instrument → target)</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Needed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytes.map((analyte) => {
                      const meta = analyte.metadata || {};
                      const cmfaId = meta.cmfa_id;
                      const targetUnit = meta.target_unit;
                      const conv = meta.conversion_factor;
                      const isDerived = !!meta.derived;
                      const need = meta.needed_for_cmfa;
                      const unitCell = analyte.default_unit
                        ? (targetUnit && targetUnit !== analyte.default_unit
                            ? `${analyte.default_unit} → ${targetUnit}${conv && conv !== 1 ? ` (×${conv})` : ""}`
                            : analyte.default_unit)
                        : "";
                      return (
                        <TableRow key={analyte.id} hover sx={isDerived ? { opacity: 0.65 } : undefined}>
                          <TableCell>{analyte.code || analyte.id}</TableCell>
                          <TableCell>
                            {analyte.name || ""}
                            {isDerived && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                                (derived)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{analyte.category || ""}</TableCell>
                          <TableCell>{cmfaId || ""}</TableCell>
                          <TableCell sx={{ color: analyte.mfalite_column ? "text.primary" : "text.secondary" }}>
                            {analyte.mfalite_column || "— (not projected)"}
                          </TableCell>
                          <TableCell>{unitCell}</TableCell>
                          <TableCell sx={{ textTransform: "capitalize", color: need === "core" ? "success.main" : "text.secondary" }}>
                            {need || ""}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Hidden. Click Show to view the reference table.
            </Typography>
          )}
        </DataCard>

        {selectedImport?.id && (
          <DataCard title="Add Vi-CELL Cell Growth Data (VCD / Viability / Cell diameter)">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Upload the run's Beckman Vi-CELL Summary CSVs (unzipped; several
                files a day is fine). Each file's process day comes from its
                name (<code>D0</code>, <code>D3</code>, …) and each Vi-CELL
                Sample ID is matched to its CEDEX sample, so the generated file
                gets VCD, Viability and Cell_diameter next to the CEDEX
                results. The files stay with this import: change the first
                measurement day or the sample mapping and they merge again.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  ref={vicellInputRef}
                  type="file"
                  multiple
                  accept=".csv,text/csv"
                  hidden
                  onChange={handleVicellFileSelect}
                />
                <Button
                  variant="outlined"
                  startIcon={<FileUploadIcon />}
                  onClick={() => vicellInputRef.current?.click()}
                  sx={{ backgroundColor: "background.paper" }}
                >
                  Choose Vi-CELL CSV(s)
                </Button>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {vicellFiles.length === 0
                    ? "No files selected"
                    : `${vicellFiles.length} file(s) selected: ` +
                      vicellFiles.map((f) => f.name).join(", ").slice(0, 200) +
                      (vicellFiles.map((f) => f.name).join(", ").length > 200 ? "…" : "")}
                </Typography>
                <Button
                  variant="contained"
                  disabled={!vicellFiles.length || isMergingVicell}
                  onClick={handleMergeVicell}
                >
                  {isMergingVicell ? "Merging…" : "Merge Vi-CELL data"}
                </Button>
              </Box>
              {vicellSummary?.suggested_first_measurement_day != null && (
                <Alert
                  severity="info"
                  sx={{ mt: 1 }}
                  action={
                    <Button
                      size="small"
                      variant="contained"
                      disabled={isUpdatingVicell}
                      onClick={() => handleSetFirstDay(vicellSummary.suggested_first_measurement_day)}
                    >
                      Set first day to {vicellSummary.suggested_first_measurement_day}
                    </Button>
                  }
                >
                  The Vi-CELL files are saved but not merged yet: their days only
                  line up with this import's CEDEX days if the first measurement
                  is day {vicellSummary.suggested_first_measurement_day}.
                </Alert>
              )}
              {vicellStoredFiles.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">
                    Vi-CELL files in this import ({vicellStoredFiles.length})
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Fix a file's day here instead of renaming the file.
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={tableHeaderCellSx}>File</TableCell>
                          <TableCell sx={tableHeaderCellSx} width={150}>Day</TableCell>
                          <TableCell sx={tableHeaderCellSx} width={56} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vicellStoredFiles.map((f) => (
                          <TableRow key={f.filename}>
                            <TableCell sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                              {f.filename}
                            </TableCell>
                            <TableCell>
                              <TextField
                                key={`${f.filename}-${f.day}`}
                                size="small"
                                type="number"
                                inputProps={{ min: 0, max: 100, "aria-label": `Day for ${f.filename}` }}
                                defaultValue={f.day ?? ""}
                                disabled={isUpdatingVicell}
                                error={f.day == null}
                                helperText={
                                  f.day == null
                                    ? "Not in the name: enter it"
                                    : f.detected_day != null && f.detected_day !== f.day
                                      ? `Name says D${f.detected_day}`
                                      : ""
                                }
                                onBlur={(e) => {
                                  if (e.target.value !== String(f.day ?? "")) {
                                    handleVicellDayChange(f.filename, e.target.value);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") e.target.blur();
                                }}
                                sx={{ width: 120 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Remove this file">
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={`Remove ${f.filename}`}
                                    disabled={isUpdatingVicell}
                                    onClick={() => handleVicellRemove(f.filename)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {vicellSummary && (
                <Alert
                  severity={
                    (vicellSummary.file_errors?.length || vicellSummary.warnings?.length)
                      ? "warning"
                      : "success"
                  }
                  sx={{ mt: 1 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Rows updated: {vicellSummary.rows_updated ?? 0} ·{" "}
                    Files processed: {vicellSummary.files_processed ?? 0}
                    {vicellSummary.files_rejected
                      ? ` · Rejected: ${vicellSummary.files_rejected}`
                      : ""}
                  </Typography>
                  {vicellSummary.day_row_counts && Object.keys(vicellSummary.day_row_counts).length > 0 && (
                    <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                      per-day rows: {Object.entries(vicellSummary.day_row_counts)
                        .map(([d, n]) => `D${d}=${n}`)
                        .join(", ")}
                    </Typography>
                  )}
                  {(vicellSummary.warnings || []).filter((w) => !w.startsWith("Vi-CELL not merged yet")).slice(0, 15).map((w, idx) => {
                    // Every warning from the backend leads with "<filename>: <reason>"
                    // — split on the first ": " so the filename renders distinctly
                    // (bold, monospace) from the reason instead of the whole line
                    // reading as one undifferentiated muted sentence.
                    const sepIdx = w.indexOf(": ");
                    const filenamePart = sepIdx > -1 ? w.slice(0, sepIdx) : null;
                    const messagePart = sepIdx > -1 ? w.slice(sepIdx + 2) : w;
                    return (
                      <Typography key={idx} variant="body2" sx={{ display: "block", mt: 0.5 }}>
                        {filenamePart ? (
                          <>
                            <Box
                              component="span"
                              sx={{ fontWeight: 700, fontFamily: "monospace", color: "text.primary" }}
                            >
                              {filenamePart}
                            </Box>
                            {": "}
                          </>
                        ) : (
                          "• "
                        )}
                        <Box component="span" sx={{ color: "text.secondary" }}>{messagePart}</Box>
                      </Typography>
                    );
                  })}
                  {(vicellSummary.warnings || []).length > 15 && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      …and {(vicellSummary.warnings || []).length - 15} more warning(s).
                    </Typography>
                  )}
                </Alert>
              )}
            </Box>
          </DataCard>
        )}

        <DataCard
          title="Import Detail / Parsed Results"
          action={
            selectedImport?.input_definition_id || selectedImport?.id ? (
              <Button
                variant="outlined"
                size="small"
                onClick={handleGenerateInputFile}
                disabled={isGenerating || !selectedImport}
              >
                {isGenerating
                  ? "Generating…"
                  : (sampleFilter || analyteFilter)
                    ? "Generate Input File (filtered)"
                    : "Generate Input File"}
              </Button>
            ) : null
          }
          noPadding
        >
          <Box sx={{ px: 3, pt: 2, pb: 2 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              {selectedImportSummary || "Select an import to view parsed rows."}
            </Typography>
            {warningGroups.length > 0 && (
              <Alert severity={warningGroups.some((g) => g.action) ? "warning" : "info"} sx={{ mb: 2 }}>
                {warningGroups.map((g) => (
                  <Typography key={g.message} variant="body2">
                    {g.count} result{g.count === 1 ? "" : "s"}: {g.plain}
                    {g.action ? "" : " (no action needed)"}
                  </Typography>
                ))}
              </Alert>
            )}
            {multiReadingDays.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {multiReadingDays.map((d) => (
                  <Typography key={`${d.day}-${d.sample_id}`} variant="body2">
                    Sample {d.sample_id}, Day {d.day} has {d.cluster_count} separate reading sessions (
                    {(d.cluster_start_times_ms || []).map((ms) => formatDate(ms)).join(", ")}
                    ) — confirm with the operator which is authoritative.
                  </Typography>
                ))}
              </Alert>
            )}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
              <TextField
                size="small"
                label="Sample ID filter"
                value={sampleFilter}
                onChange={(e) => setSampleFilter(e.target.value)}
                helperText={'space or comma; quote to include spaces — e.g. "stratyx d0-1", stratyx'}
                sx={{ minWidth: 280 }}
                InputProps={{
                  endAdornment: sampleFilter ? (
                    <IconButton
                      size="small"
                      onClick={() => setSampleFilter("")}
                      aria-label="Clear Sample ID filter"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null,
                }}
              />
              <TextField
                size="small"
                label="Analyte filter"
                value={analyteFilter}
                onChange={(e) => setAnalyteFilter(e.target.value)}
                helperText={'space or comma; quote to include spaces — e.g. GLC, LAC, "L-lactate"'}
                sx={{ minWidth: 280 }}
                InputProps={{
                  endAdornment: analyteFilter ? (
                    <IconButton
                      size="small"
                      onClick={() => setAnalyteFilter("")}
                      aria-label="Clear Analyte filter"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null,
                }}
              />
              <Button variant="outlined" onClick={applyFilters} disabled={!selectedImportId}>
                Apply Filters
              </Button>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderCellSx}>Sample ID</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Analyte</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Unit</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Raw Value</TableCell>
                  <TableCell sx={tableHeaderCellSx} align="right">Numeric Value</TableCell>
                  <TableCell sx={tableHeaderCellSx} align="right">Converted Value</TableCell>
                  <TableCell sx={tableHeaderCellSx} align="right">Derived Numeric</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Measured At</TableCell>
                  <TableCell sx={tableHeaderCellSx} align="right">Day</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Status</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Parse Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.sample_id}</TableCell>
                    <TableCell>{row.analyte_code}</TableCell>
                    <TableCell>{row.unit}</TableCell>
                    <TableCell>{row.raw_value}</TableCell>
                    <TableCell align="right">{row.numeric_value ?? ""}</TableCell>
                    <TableCell align="right">
                      {row.converted_value != null ? (
                        row.unit_mismatch ? (
                          <Tooltip
                            title={`Unit mismatch: instrument reported ${row.unit || "?"}, curation expects a different unit — value NOT converted, written through as-is.`}
                          >
                            <Typography component="span" variant="body2" sx={{ color: "warning.main" }}>
                              {row.converted_value}{row.converted_unit ? ` ${row.converted_unit}` : ""} ⚠
                            </Typography>
                          </Tooltip>
                        ) : (
                          `${row.converted_value}${row.converted_unit ? ` ${row.converted_unit}` : ""}`
                        )
                      ) : ""}
                    </TableCell>
                    <TableCell align="right">{row.derived_numeric_value ?? ""}</TableCell>
                    <TableCell>{formatDate(row.measured_at)}</TableCell>
                    <TableCell align="right">{row.day ?? ""}</TableCell>
                    <TableCell>{row.parse_status}</TableCell>
                    <TableCell>{row.parse_message || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DataCard>
      </PageContainer>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (isDeleting ? null : setDeleteTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete CEDEX import?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.filename || deleteTarget?.id}</strong>?
            This removes the import row, its parsed analyte results, any
            materialized raw rows, and the auto-created Library entry.
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirmed}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
